import LeverantorFakturaBildIkon from './ObjektVyer/LeverantorFaktura/Components/LeverantorFakturaBildIkon';
import { displayErrorValues } from '../_Common/Utils/BusinessConfigUtils';
import { IBusinessConfigs } from '../_Common/Const/BusinessConfigs';
import { initConst } from '../../../Utils/utils';
import {
  TEDLRBetalningSTATUSEnum,
  TEDLRFakturaDelAttestMinaStatusEnum,
} from '../../../globalQueryTypes';
import MessageListIkon, {
  RutinEnum,
} from './ObjektVyer/LeverantorFaktura/Components/MessageListIkon';
import DokumentViewIkon from '../../../Components/DokumentViewIkon';
import DokumentDownloadIkon from '../../../Components/DokumentDownloadIkon';
import { isImageExtension } from '../../../Components/Documents/Documents';
import FakturaDokumentAddComp from './ObjektVyer/FakturaDok/Components/FakturaDokumentAddComp';
import displayFakturaDelTimerIkon from './ObjektVyer/FakturaDel/Components/FakturaDelTimerIkon';
import AttestMinaStatusComp from './ObjektVyer/FakturaDel/Components/AttestMinaStatusComp/AttestMinaStatusComp';
import EDLink from '../../../BaseComponents/Link/EDLink';

const LRBusinessConfig = initConst<IBusinessConfigs>(() => ({
  LRLeverantorfaktura: {
    listIcons: [
      {
        componentFn: config => (
          <MessageListIkon
            key="LeverantorFakturaMeddelandeIkon"
            id={config.id || ''}
            hidden={!config.data?.HarMeddelande}
            messages={config.data?._Associations?.Attestmeddelande_All}
            rutin={RutinEnum.FAKTURAATTEST}
          />
        ),
        dataDependencies: [
          'HarMeddelande',
          'Attestmeddelande.MEDDELANDE',
          'Attestmeddelande.Rubrik',
          'Attestmeddelande.DATUM',
          'Attestmeddelande.TID',
        ],
      },
      {
        componentFn: config => {
          return (
            <DokumentViewIkon
              key="FakturaDokumentViewIkon"
              hidden={!config.data?.HarDokument}
              documents={config.data?._Associations?.Dokument_All}
            />
          );
        },
        dataDependencies: [
          'HarDokument',
          'Dokument.BINARDATA',
          'Dokument.EXTENSION',
          'Dokument.BESKRIVNING',
        ],
      },
      {
        componentFn: config => (
          <LeverantorFakturaBildIkon
            key="LeverantorFakturaListIkon"
            id={config.id || ''}
            disabled={config.disabled}
            hidden={!config.data?.HarFakturabild}
          />
        ),
        dataDependencies: ['HarFakturabild'],
      },
    ],
    attributes: {
      Detaljstatus: {
        displayComp: displayErrorValues(['SPARRAD']),
      },
      _typ: {
        tableDefaultWidth: { md: 80, lg: 100, xl: 120 },
      },
      Fakturabelopp2: {
        tableDefaultWidth: { xs: 120, lg: 150 },
      },
    },
    defaultSort: [{ attributeName: 'FAKTURA_NR', order: 'desc' }],
    defaultColumns: [
      { attributeDef: 'FAKTURA_NR' },
      { attributeDef: 'Leverantor' },
      { attributeDef: 'FORFALLO_DATUM' },
      { attributeDef: 'Fakturabelopp2' },
      { attributeDef: 'Detaljstatus' },
    ],
    unusedBusinessMenuButtons: ['add'],
  },
  LRBetalning: {
    objectLinksEnabled: true,
    colors: {
      styles: [
        {
          caption: 'Obetald',
          color: 'darkBlue',
          colorOpacity: 'full',
          condition: data => data?.STATUS !== TEDLRBetalningSTATUSEnum.BETALD,
          dataDependencies: ['STATUS'],
        },
        {
          caption: 'Spärrad',
          color: 'red',
          colorOpacity: 'full',
          condition: data => data?.STATUS === TEDLRBetalningSTATUSEnum.SPARRAD,
          dataDependencies: ['STATUS'],
        },
      ],
    },
    defaultColumns: [
      {
        attributeDef: 'UTBET_DATUM',
        caption: 'Utbetalningsdatum',
        header: true,
      },
      { attributeDef: 'Fakturanr', caption: 'Fakturanr' },
      { attributeDef: 'Leverantor', caption: 'Leverantör' },
      { attributeDef: 'BELOPP', caption: 'Belopp' },
      { attributeDef: 'STATUS', caption: 'Status' },
    ],
  },
  LRLeverantor: {
    attributes: {
      LeverantorNrNamn: {
        tableDefaultWidth: { xs: 100, md: 140, lg: 200, xl: 300 },
      },
    },
    colors: {
      styles: [
        {
          caption: 'Borttagen',
          color: 'gray',
          colorOpacity: 'full',
          condition: data => data?.BORTTAGEN ?? false,
          dataDependencies: ['BORTTAGEN'],
        },
        {
          caption: 'Spärrad',
          color: 'red',
          colorOpacity: 'full',
          condition: data => data?.SPARRAD_FROM_DATUM ?? false,
          dataDependencies: ['SPARRAD_FROM_DATUM'],
        },
      ],
    },
    dataDependencies: ['IDString'],
    defaultSort: [{ attributeName: 'LEVERANTORSNR', order: 'asc' }],
    defaultColumns: [
      { attributeDef: 'LEVERANTORSNR', caption: 'Nr', header: true },
      { attributeDef: 'OrgPers.NamnFE', caption: 'Namn', header: true },
      { attributeDef: 'OrgPers.Adress.KompaktAdressRad', caption: 'Adress' },
      { attributeDef: 'ReskontraSaldoAktuellt', caption: 'Saldo reskontra' },
      { attributeDef: 'KUNDNR', caption: 'Vårt kundnr' },
    ],
    userDeleteable: true,
    itemDeletable: input =>
      input.item?.['ReskontraSaldoAktuellt'] === 0
        ? { disabled: false }
        : {
            disabled: true,
            disabledReason: 'Kan inte ta bort leverantör med aktuellt saldo',
          },
  },
  LRFakturabunt: {
    defaultColumns: [
      { attributeDef: 'BUNTNR', caption: 'Buntnr', header: true },
      { attributeDef: 'ANTAL_FAKTUROR', caption: 'Antal' },
      { attributeDef: 'INLAST_DATUM', caption: 'Datum' },
      { attributeDef: 'BokfGrupp.BENAMNING', caption: 'Bokföringsgrupp' },
    ],
  },
  LRFakturaDel: {
    attributes: {
      FaktnrAttestdel: {
        tableDefaultWidth: { md: 140, lg: 180 },
      },
      ForfalloDatumMedIkon: {
        displayComp: displayFakturaDelTimerIkon,
        hideTooltip: true,
        tableDefaultWidth: { lg: 160, xl: 180 },
      },
      AttestMinaStatus: {
        displayComp: AttestMinaStatusComp,
        hideTooltip: true,
        tableMinWidth: 267,
      },
      NastaAttestantSignNamn: {
        tableDefaultWidth: { xs: 100, md: 180, lg: 250, xl: 380 },
        size: 'large',
      },
    },
    listIcons: [
      {
        componentFn: config => (
          <MessageListIkon
            key="LeverantorFakturaMeddelandeIkon"
            id={config.id || ''}
            hidden={!config.data?._Associations?.Faktura?.HarMeddelande}
            messages={
              config.data?._Associations?.Faktura?._Associations
                .Attestmeddelande_All
            }
            rutin={RutinEnum.FAKTURAATTEST}
          />
        ),
        dataDependencies: [
          'Faktura.HarMeddelande',
          'Faktura.Attestmeddelande.MEDDELANDE',
          'Faktura.Attestmeddelande.Rubrik',
          'Faktura.Attestmeddelande.DATUM',
          'Faktura.Attestmeddelande.TID',
        ],
      },
      {
        componentFn: config => {
          return (
            <DokumentViewIkon
              key="FakturaDokumentViewIkon"
              hidden={!config.data?._Associations?.Faktura?.HarDokument}
              documents={
                config.data?._Associations?.Faktura?._Associations?.Dokument_All
              }
            />
          );
        },
        dataDependencies: [
          'Faktura.HarDokument',
          'Faktura.Dokument.BINARDATA',
          'Faktura.Dokument.EXTENSION',
          'Faktura.Dokument.BESKRIVNING',
        ],
      },
      {
        componentFn: config => (
          <LeverantorFakturaBildIkon
            key="LeverantorFakturaBildIkon"
            id={config.data?._Associations?.Faktura.id || ''}
            disabled={config.disabled}
            hidden={!config.data?._Associations?.Faktura.HarFakturabild}
          />
        ),
        dataDependencies: ['Faktura.HarFakturabild', 'Faktura.DefAnkVernr'],
      },
    ],
    defaultSort: [{ attributeName: 'ForfalloDatumMedIkon', order: 'asc' }],
    defaultColumns: [
      {
        attributeDef: 'Faktura.Leverantor.LeverantorNrNamn',
        header: true,
      },
      { attributeDef: 'FaktnrAttestdel', caption: 'Fakturanr', minSize: 'sm' },
      { attributeDef: 'ForfalloDatumMedIkon', caption: 'Förfallodatum' },
      { attributeDef: 'Faktura.Fakturabelopp2' },
      { attributeDef: 'Faktura._typ', minSize: 'sm' },
      { attributeDef: 'AttestMinaStatus' },
      { attributeDef: 'NastaAttestantSignNamn', minSize: 'sm' },
    ],

    colors: {
      hideLegendColumn: true,
      styles: [
        {
          caption: 'Ej attesterad, hos annan attestant',
          color: 'indigo',
          colorOpacity: 'faded',
          condition: data => {
            return (
              data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.EJ_ATTESTERAD__HOS_ANNAN_ATTESTANT
            );
          },
          dataDependencies: ['AttestMinaStatus'],
        },
        {
          caption: 'Under utredning',
          color: 'red',
          colorOpacity: 'faded',
          condition: data =>
            data.AttestMinaStatus ===
            TEDLRFakturaDelAttestMinaStatusEnum.UNDER_UTREDNING,
          dataDependencies: ['AttestMinaStatus'],
        },
        {
          caption:
            'Attesterad (ej definitivbokad, definitivbokad, betald, hos annan attestant mm.)',
          color: 'darkGreen',
          colorOpacity: 'faded',
          condition: data =>
            data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.ATTESTERAD__HOS_ANNAN_ATTESTANT ||
            data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.ATTESTERAD__BETALD ||
            data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.ATTESTERAD__DEFINITIVBOKAD ||
            data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.ATTESTERAD__DELBETALD ||
            data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.ATTESTERAD__EJ_DEFINITIVBOKAD ||
            data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.ATTESTERAD__MAKULERAD ||
            data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.ATTESTERAD__PA_VAG_TILL_BETALNING ||
            data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.ATTESTERAD__SPARRAD,

          dataDependencies: ['AttestMinaStatus'],
        },
        {
          caption:
            'Mottagningsattesteras, Beslutsattesteras eller Kontrollattesteras',
          color: 'lightBlue',
          colorOpacity: 'faded',
          condition: data =>
            data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.KONTROLLATTESTERAS ||
            data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.BESLUTSATTESTERAS ||
            data.AttestMinaStatus ===
              TEDLRFakturaDelAttestMinaStatusEnum.MOTTAGNINGSATTESTERAS,
          dataDependencies: ['AttestMinaStatus'],
        },
      ],
    },

    unusedBusinessMenuButtons: ['add'],
    dataDependencies: [
      'PaminnelseStatus',
      //FIXME: Hack för att få med data från innehållssidan direkt, då vi inte kan välja det på annat sätt just nu
      'Faktura.FORFALLO_DATUM',
      'Faktura.FAKTURADATUM',
      'Faktura.DefAnkVernr',
      'Faktura.FAKTURA_NR',
      'Faktura.FakturabeloppKredittext',
      'Faktura.MomsBelopp2',
      'Faktura.MOMSHANTERING',
      'AttestMinaStatus',
    ],
  },
  LRBetalningsuppdrag: {
    defaultColumns: [
      {
        attributeDef: 'FIL_PRODDATUM',
        caption: 'Produktionsdatum',
        header: true,
      },
      { attributeDef: 'SkapadUtbetfil', caption: 'Skapad utbetalningsfil' },
      { attributeDef: 'Utbetalningsvag.BENAMNING', caption: 'Utbetalningsväg' },
      { attributeDef: 'Totalbelopp', caption: 'Totalbelopp' },
      { attributeDef: 'AntalFakturor', caption: 'Antal' },
      { attributeDef: 'Anvandare', caption: 'Skapad av' },
      {
        attributeDef: 'STATUS_ELEKTRONISK_BETALNING',
        caption: 'Status, elektronisk betalning',
      },
      { attributeDef: 'REDOVISA_PER_GRUPP', caption: 'Per bokf. grupp' },
    ],
  },
  LRBetalredovisning: {
    defaultColumns: [
      { attributeDef: 'Fildatum', caption: 'Skapad', header: true },
      { attributeDef: 'SLUTBETALD', caption: 'Typ' },
      { attributeDef: 'Totalbelopp', caption: 'Belopp' },
      { attributeDef: 'AntalTrans', caption: 'Transaktioner' },
      { attributeDef: 'Kommunikationsfil.InlastText', caption: 'Inläst' },
      { attributeDef: 'Hanterad_Text', caption: 'Hanterad' },
    ],
  },
  LRAttestant: {
    defaultColumns: [
      { attributeDef: 'SIGNATUR', caption: 'Signatur', header: true },
      { attributeDef: 'BENAMNING', caption: 'Namn' },
      { attributeDef: 'FRANVARANDE', caption: 'Frånvarande' },
    ],
  },
  LRAttestOmrade: {
    defaultColumns: [
      { attributeDef: 'KOD', caption: 'Kod', header: true },
      { attributeDef: 'BENAMNING', caption: 'Benämning', header: true },
      { attributeDef: 'Attestgrupp.BENAMNING', caption: 'Attestgrupp' },
      { attributeDef: 'AnsvarigNamn', caption: 'Ansvarig' },
    ],
  },
  LRFakturamottagningsvag: {
    defaultColumns: [
      { attributeDef: 'BENAMNING', caption: 'Benämning', header: true },
      { attributeDef: 'IMPORTTYP', caption: 'Importtyp' },
      { attributeDef: 'AKTIV', caption: 'Aktiv' },
    ],
  },
  LRAttestforslag: {
    defaultColumns: [
      { attributeDef: 'IDString', caption: 'Referens', header: true },
      { attributeDef: 'ANVANDS_FOR', caption: 'Används för' },
      { attributeDef: 'GILTIG_FOR', caption: 'Giltig för' },
      {
        attributeDef: 'MottagandeEnhet.Namn',
        caption: 'Mottagande enhet',
      },
      {
        attributeDef: 'Fakturakod.KOD',
        caption: 'Fakturakod',
      },
      { attributeDef: 'FAKTURARADSKOD', caption: 'Fakturaradskod' },
      {
        attributeDef: 'Leverantor.LeverantorNrNamn',
        caption: 'Leverantör',
      },
    ],
  },
  LRLevBetsatt: {
    defaultColumns: [
      {
        attributeDef: 'Utbetalningssatt',
        caption: 'Utbetalningssätt',
        header: true,
      },
      { attributeDef: 'KONTO_NR', caption: 'Kontonummer' },
    ],
  },
  LRLeverantorkat: {
    defaultColumns: [
      { attributeDef: 'BENAMNING', caption: 'Benämning', header: true },
      { attributeDef: 'INTERN', caption: 'Intern' },
    ],
  },
  LRKonteringsForslag: {
    defaultColumns: [],
  },
  LRFakturaDok: {
    objectLinksEnabled: true,
    addComponent: FakturaDokumentAddComp,
    defaultColumns: [
      { attributeDef: 'BESKRIVNING', header: true },
      { attributeDef: 'SENAST_ANDRAD' },
      { attributeDef: 'SENAST_ANDRAD_SIGNATUR' },
    ],
    listIcons: [
      {
        componentFn: config => {
          const extension: string = config.data?.EXTENSION?.toLowerCase() ?? '';
          const hidden = !isImageExtension(extension) && extension !== 'pdf';

          return (
            <DokumentViewIkon
              documents={[
                {
                  BINARDATA: config?.data?.BINARDATA,
                  BESKRIVNING: config?.data?.BESKRIVNING,
                  EXTENSION: config?.data?.EXTENSION,
                },
              ]}
              key="FakturaDokumentViewIkon"
              hidden={hidden}
            />
          );
        },
        dataDependencies: ['BINARDATA', 'BESKRIVNING', 'EXTENSION'],
      },
      {
        componentFn: config => (
          <DokumentDownloadIkon
            key="FakturaDokumentDownloadIkon"
            blobId={config.data?.BINARDATA}
            extension={config.data?.EXTENSION}
            fileName={config.data?.BESKRIVNING}
          />
        ),
        dataDependencies: ['BINARDATA'],
      },
    ],
  },
  LRLeverantorAttest: {
    defaultColumns: [
      {
        attributeDef: 'Leverantor.LeverantorNrNamn',
      },
      { attributeDef: 'STATUS' },
      { attributeDef: 'ForstaLoggTid' },
    ],
  },
  LRLeverantorAttestLogg: {
    defaultColumns: [
      {
        attributeDef: 'LOGG_TEXT',
        caption: 'Händelse',
      },
      { attributeDef: 'UserName', caption: 'Användare' },
      { attributeDef: 'LOGG_USER_SIGN' },
      { attributeDef: 'LOGG_TIMESTAMP' },
      { attributeDef: 'GiltigEnlBGPG' },
      { attributeDef: 'Utbetalningssatt.BENAMNING' },
      { attributeDef: 'BGPGUrl', caption: 'Länk till Bankgiro/Plusgiro' },
    ],
    attributes: {
      LOGG_TIMESTAMP: {
        displayComp: props => {
          if (props.value == null) {
            return '-';
          }
          const date = new Date(props.value as string);
          const formattedDate = date.toLocaleString('sv-SE', {
            timeStyle: 'short',
            dateStyle: 'short',
          });

          return formattedDate;
        },
      },
      BGPGUrl: {
        displayComp: props => {
          const url = props.value as string | undefined;
          if (!url) {
            return '-';
          }
          if (url.includes('bankgirot')) {
            return (
              <EDLink href={url} target="_blank">
                Visa Bankgiro
              </EDLink>
            );
          }
          if (url.includes('plusgirot')) {
            return (
              <EDLink href={url} target="_blank">
                Visa Plusgiro
              </EDLink>
            );
          }

          return (
            <EDLink href={url} target="_blank">
              Visa
            </EDLink>
          );
        },
      },
    },
    defaultSort: [{ attributeName: 'LOGG_TIMESTAMP', order: 'desc' }],
  },
}));

export default LRBusinessConfig;
